'use client';

import { Clock } from 'lucide-react';
import { useRouter } from '@/src/i18n/navigation';
import type { PropertyDetailListing } from '@/src/components/features/property/types';
import { dateUtils } from '@/src/components/features/property/lib';
import { LucideIcon } from '@/src/components/LucideIcon';
import * as styles from './expiredListingBanner.styles';

type ListingHistory = {
  deactivated?: { date: number; ago: string };
};

type Props = {
  listing: PropertyDetailListing;
};

/**
 * Displayed when a listing is no longer active. Links the user back to
 * the previous page (usually the SRP that brought them here).
 */
export const ExpiredListingBanner = ({ listing }: Props) => {
  const router = useRouter();

  if (!listing || listing.active !== false) return null;

  const history = (listing as unknown as { history?: ListingHistory })?.history;

  const handleExploreClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    router.back();
  };

  const deactivatedDate = history?.deactivated?.ago ? dateUtils.formatDateMmddyy(history.deactivated.date) : null;

  return (
    <div className={styles.container} role="status">
      <LucideIcon icon={Clock} size="lg" title="Listing expired" />
      <div className={styles.textContent}>
        <span className={styles.messageText}>
          {deactivatedDate
            ? `This pad was taken off the market on ${deactivatedDate}.`
            : 'This pad is no longer available.'}
        </span>
        <a href="#" onClick={handleExploreClick} className={styles.exploreLink}>
          Explore listings in the same area.
        </a>
      </div>
    </div>
  );
};
